import React, { FC, useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Chip,
  IconButton,
  Checkbox,
  Typography,
  Card,
  CardContent,
  Grid,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  CheckCircle as ApproveIcon,
  Block as SuspendIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  GetApp as ExportIcon,
  MoreVert as MoreIcon,
  TrendingUp as PerformanceIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as CompanyIcon
} from '@material-ui/icons';
import { useQuery, useMutation } from '@apollo/client';
import { format } from 'date-fns';

// GraphQL queries and mutations
import { ALL_AFFILIATES_QUERY, AFFILIATE_CONVERSIONS_QUERY } from '../../../core/graphql/queries/affiliate';
import { 
  APPROVE_AFFILIATE, 
  SUSPEND_AFFILIATE, 
  DELETE_AFFILIATE,
  BULK_APPROVE_AFFILIATES,
  BULK_SUSPEND_AFFILIATES
} from '../../../core/graphql/mutations/affiliate';
import { 
  AllAffiliatesQueryResponse, 
  AllAffiliatesQueryVariables,
  AffiliateConversionsQueryResponse,
  AffiliateConversionsQueryVariables
} from '../../../core/graphql/queries/affiliate';
import {
  ApproveAffiliateResponse,
  ApproveAffiliateVariables,
  SuspendAffiliateResponse,
  SuspendAffiliateVariables,
  DeleteAffiliateResponse,
  DeleteAffiliateVariables,
  BulkApproveAffiliatesResponse,
  BulkApproveAffiliatesVariables,
  BulkSuspendAffiliatesResponse,
  BulkSuspendAffiliatesVariables
} from '../../../core/graphql/mutations/affiliate';

// Types
interface AffiliateManagementProps {}

interface Affiliate {
  id: string;
  userId: string;
  referralCode: string;
  status: string;
  commissionAmount: number;
  totalEarnings: number;
  totalConversions: number;
  pendingPayout: number;
  paidOut: number;
  name: string;
  email: string;
  phoneNumber: string;
  company: string;
  createdAt: string;
  updatedAt: string;
}

interface Conversion {
  id: string;
  affiliateId: string;
  referralCode: string;
  customerId?: string;
  orderAmount: number;
  commissionAmount: number;
  status: 'pending' | 'approved' | 'paid' | 'rejected';
  approvedAt?: string;
  paidAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
}

const AffiliateManagement: FC<AffiliateManagementProps> = () => {
  // State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedAffiliates, setSelectedAffiliates] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAffiliate, setSelectedAffiliate] = useState<Affiliate | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showConversionsDialog, setShowConversionsDialog] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // GraphQL queries with fallback
  const { data, loading, error, refetch } = useQuery<
    AllAffiliatesQueryResponse,
    AllAffiliatesQueryVariables
  >(ALL_AFFILIATES_QUERY, {
    variables: {
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      status: statusFilter === 'all' ? undefined : statusFilter as any
    },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });

  // Conversions query for selected affiliate
  const { data: conversionsData, loading: conversionsLoading } = useQuery<
    AffiliateConversionsQueryResponse,
    AffiliateConversionsQueryVariables
  >(AFFILIATE_CONVERSIONS_QUERY, {
    variables: {
      affiliateId: selectedAffiliate?.id,
      limit: 50
    },
    skip: !selectedAffiliate || !showConversionsDialog,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });

  // Mock conversions for dialog fallback
  const mockConversions: Conversion[] = [
    {
      id: 'c1',
      affiliateId: '1',
      referralCode: 'AFF-12345678',
      customerId: 'artist1',
      orderAmount: 49.99,
      commissionAmount: 5.00,
      status: 'approved',
      approvedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      id: 'c2',
      affiliateId: '2',
      referralCode: 'AFF-87654321',
      customerId: 'artist2',
      orderAmount: 99.00,
      commissionAmount: 10.00,
      status: 'pending',
      createdAt: new Date(Date.now() - 7200000).toISOString(),
      updatedAt: new Date(Date.now() - 3600000).toISOString()
    }
  ];

  // Mock data fallback when GraphQL fails
  const mockAffiliates: Affiliate[] = [
    {
      id: '1',
      userId: 'user1',
      referralCode: 'AFF-12345678',
      status: 'pending',
      commissionAmount: 5.00,
      totalEarnings: 0,
      totalConversions: 0,
      pendingPayout: 0,
      paidOut: 0,
      name: 'John Doe',
      email: 'john@example.com',
      phoneNumber: '+1-555-0123',
      company: 'Tech Corp',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      id: '2',
      userId: 'user2',
      referralCode: 'AFF-87654321',
      status: 'active',
      commissionAmount: 5.00,
      totalEarnings: 150.50,
      totalConversions: 3,
      pendingPayout: 25.00,
      paidOut: 125.50,
      name: 'Jane Smith',
      email: 'jane@example.com',
      phoneNumber: '+1-555-0456',
      company: 'Marketing Inc',
      createdAt: new Date(Date.now() - 86400000).toISOString(),
      updatedAt: new Date(Date.now() - 3600000).toISOString()
    },
    {
      id: '3',
      userId: 'user3',
      referralCode: 'AFF-11223344',
      status: 'active',
      commissionAmount: 10.00,
      totalEarnings: 450.25,
      totalConversions: 8,
      pendingPayout: 80.00,
      paidOut: 370.25,
      name: 'Michael Johnson',
      email: 'michael@example.com',
      phoneNumber: '+1-555-0789',
      company: 'Digital Solutions',
      createdAt: new Date(Date.now() - 172800000).toISOString(),
      updatedAt: new Date(Date.now() - 7200000).toISOString()
    },
    {
      id: '4',
      userId: 'user4',
      referralCode: 'AFF-99887766',
      status: 'suspended',
      commissionAmount: 5.00,
      totalEarnings: 75.00,
      totalConversions: 2,
      pendingPayout: 0,
      paidOut: 75.00,
      name: 'Sarah Williams',
      email: 'sarah@example.com',
      phoneNumber: '+1-555-0321',
      company: 'Creative Agency',
      createdAt: new Date(Date.now() - 259200000).toISOString(),
      updatedAt: new Date(Date.now() - 86400000).toISOString()
    },
    {
      id: '5',
      userId: 'user5',
      referralCode: 'AFF-55443322',
      status: 'pending',
      commissionAmount: 8.00,
      totalEarnings: 0,
      totalConversions: 0,
      pendingPayout: 0,
      paidOut: 0,
      name: 'Robert Brown',
      email: 'robert@example.com',
      phoneNumber: '+1-555-0654',
      company: 'Music Productions',
      createdAt: new Date(Date.now() - 345600000).toISOString(),
      updatedAt: new Date(Date.now() - 345600000).toISOString()
    }
  ];

  // GraphQL mutations
  const [approveAffiliate] = useMutation<
    ApproveAffiliateResponse,
    ApproveAffiliateVariables
  >(APPROVE_AFFILIATE);

  const [suspendAffiliate] = useMutation<
    SuspendAffiliateResponse,
    SuspendAffiliateVariables
  >(SUSPEND_AFFILIATE);

  const [deleteAffiliate] = useMutation<
    DeleteAffiliateResponse,
    DeleteAffiliateVariables
  >(DELETE_AFFILIATE);

  const [bulkApproveAffiliates] = useMutation<
    BulkApproveAffiliatesResponse,
    BulkApproveAffiliatesVariables
  >(BULK_APPROVE_AFFILIATES);

  const [bulkSuspendAffiliates] = useMutation<
    BulkSuspendAffiliatesResponse,
    BulkSuspendAffiliatesVariables
  >(BULK_SUSPEND_AFFILIATES);

  // Computed values with fallback (use mock until real records exist)
  const affiliates = (data?.allAffiliates && data.allAffiliates.length > 0)
    ? data.allAffiliates
    : mockAffiliates;
  const filteredAffiliates = affiliates.filter(affiliate =>
    (affiliate.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (affiliate.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (affiliate.referralCode || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const usingMockData = !data?.allAffiliates || data.allAffiliates.length === 0 || !!error;

  // Error handling
  const handleError = (error: any, operation: string) => {
    console.error(`${operation} error:`, error);
    const errorMessage = error?.graphQLErrors?.[0]?.message || 
                        error?.networkError?.message || 
                        `${operation} failed. Please try again.`;
    
    setSnackbar({
      open: true,
      message: errorMessage,
      severity: 'error'
    });
  };

  // Handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleStatusFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setStatusFilter(event.target.value as string);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedAffiliates(filteredAffiliates.map(affiliate => affiliate.id));
    } else {
      setSelectedAffiliates([]);
    }
  };

  const handleSelectAffiliate = (affiliateId: string) => {
    setSelectedAffiliates(prev => 
      prev.includes(affiliateId)
        ? prev.filter(id => id !== affiliateId)
        : [...prev, affiliateId]
    );
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, affiliate: Affiliate) => {
    setAnchorEl(event.currentTarget);
    setSelectedAffiliate(affiliate);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAffiliate(null);
  };

  const handleApproveAffiliate = async (affiliateId: string) => {
    try {
      await approveAffiliate({ 
        variables: { id: affiliateId }
      });
      setSnackbar({
        open: true,
        message: 'Affiliate approved successfully',
        severity: 'success'
      });
      refetch();
    } catch (error) {
      handleError(error, 'Approve affiliate');
    }
    handleMenuClose();
  };

  const handleSuspendAffiliate = async (affiliateId: string) => {
    try {
      await suspendAffiliate({ 
        variables: { id: affiliateId, reason: 'Suspended by admin' }
      });
      setSnackbar({
        open: true,
        message: 'Affiliate suspended successfully',
        severity: 'success'
      });
      refetch();
    } catch (error) {
      handleError(error, 'Suspend affiliate');
    }
    handleMenuClose();
  };

  const handleDeleteAffiliate = async (affiliateId: string) => {
    try {
      await deleteAffiliate({ 
        variables: { affiliateId: affiliateId }
      });
      setSnackbar({
        open: true,
        message: 'Affiliate deleted successfully',
        severity: 'success'
      });
      refetch();
    } catch (error) {
      handleError(error, 'Delete affiliate');
    }
    handleMenuClose();
  };

  const handleBulkApprove = async () => {
    try {
      await bulkApproveAffiliates({ variables: { affiliateIds: selectedAffiliates } });
      setSnackbar({
        open: true,
        message: `${selectedAffiliates.length} affiliates approved successfully`,
        severity: 'success'
      });
      setSelectedAffiliates([]);
      refetch();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to approve affiliates',
        severity: 'error'
      });
    }
  };

  const handleBulkSuspend = async () => {
    try {
      await bulkSuspendAffiliates({ variables: { affiliateIds: selectedAffiliates, reason: 'Bulk suspended by admin' } });
      setSnackbar({
        open: true,
        message: `${selectedAffiliates.length} affiliates suspended successfully`,
        severity: 'success'
      });
      setSelectedAffiliates([]);
      refetch();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to suspend affiliates',
        severity: 'error'
      });
    }
  };

  const handleExportData = () => {
    const csvContent = [
      ['Name', 'Email', 'Phone', 'Company', 'Code', 'Status', 'Created', 'Conversions', 'Revenue', 'Earnings'],
      ...filteredAffiliates.map(affiliate => [
        affiliate.name,
        affiliate.email,
        affiliate.phoneNumber,
        affiliate.company || '',
        affiliate.referralCode,
        affiliate.status,
        format(new Date(affiliate.createdAt), 'MMM dd, yyyy'),
        0, // Conversions not available in this query
        '$0.00', // Revenue not available in this query
        '$0.00' // Earnings not available in this query
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `affiliates-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
      case 'approved':
        return '#1976d2'; // Keep blue for active/approved
      case 'pending':
        return '#2d4a3e'; // Header color for pending
      case 'suspended':
        return '#f44336'; // Red for suspended
      default:
        return '#2d4a3e'; // Default to header color
    }
  };

  const getStatusTextColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
      case 'approved':
        return '#fff'; // White text on green
      case 'pending':
        return '#fff'; // White text on header color
      case 'suspended':
        return '#fff'; // White text on red
      default:
        return '#fff'; // White text on header color
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading affiliates...</Typography>
      </Box>
    );
  }

  // Don't show error screen - we have mock data as fallback
  // if (error) {
  //   return (
  //     <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" flexDirection="column">
  //       <Alert severity="error" style={{ marginBottom: 16 }}>
  //         Error loading affiliates: {error.message}
  //       </Alert>
  //       <Button 
  //         variant="contained" 
  //         color="primary" 
  //         onClick={() => refetch()}
  //       >
  //         Retry
  //       </Button>
  //     </Box>
  //   );
  // }

  return (
    <Box p={3} pt={4} pb={4} style={{ backgroundColor: '#02172a', minHeight: '100vh', color: '#fff' }}>
      {usingMockData && (
        <Alert severity="info" style={{ marginBottom: 16 }}>
          Showing mock affiliate data until real records exist.
        </Alert>
      )}
      <Typography variant="h4" gutterBottom style={{ color: '#fff', fontWeight: 'bold' }}>
        Affiliate Management
      </Typography>

      {/* Search and Filter Bar */}
      <Paper style={{ padding: 12, marginTop: 8, marginBottom: 16, backgroundColor: '#1a2332', border: '1px solid #333' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search affiliates..."
              value={searchTerm}
              onChange={handleSearchChange}
              style={{ color: '#fff' }}
              InputProps={{
                startAdornment: <SearchIcon style={{ marginRight: 8, color: '#1976d2' }} />,
                style: { color: '#fff' }
              }}
              InputLabelProps={{ style: { color: '#fff' } }}
              inputProps={{ style: { color: '#fff' } }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel style={{ color: '#fff' }}>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                style={{ color: '#fff' }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      backgroundColor: '#2d4a3e',
                      color: '#fff',
                      border: '1px solid #333'
                    }
                  }
                }}
              >
                <MenuItem value="all" style={{ color: '#fff', backgroundColor: '#2d4a3e' }}>All Statuses</MenuItem>
                <MenuItem value="pending" style={{ color: '#fff', backgroundColor: '#2d4a3e' }}>Pending</MenuItem>
                <MenuItem value="active" style={{ color: '#fff', backgroundColor: '#2d4a3e' }}>Active</MenuItem>
                <MenuItem value="suspended" style={{ color: '#fff', backgroundColor: '#2d4a3e' }}>Suspended</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
              {selectedAffiliates.length > 0 && (
                <>
                  <Button
                    variant="contained"
                    startIcon={<ApproveIcon />}
                    onClick={handleBulkApprove}
                    style={{
                      backgroundColor: '#1976d2',
                      color: '#fff'
                    }}
                  >
                    Approve ({selectedAffiliates.length})
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SuspendIcon />}
                    onClick={handleBulkSuspend}
                    style={{
                      backgroundColor: '#f44336',
                      color: '#fff'
                    }}
                  >
                    Suspend ({selectedAffiliates.length})
                  </Button>
                </>
              )}
              <Button
                variant="contained"
                startIcon={<ExportIcon />}
                onClick={handleExportData}
                style={{
                  backgroundColor: '#2d4a3e',
                  color: '#fff',
                  border: '1px solid #333'
                }}
              >
                Export
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Affiliates Table */}
      <Paper style={{ backgroundColor: '#1a2332', border: '1px solid #333', marginTop: 8 }}>
        <TableContainer>
          <Table>
            <TableHead style={{ backgroundColor: '#02172a' }}>
              <TableRow>
                <TableCell padding="checkbox" style={{ color: '#1976d2', fontWeight: 'bold' }}>
                  <Checkbox
                    indeterminate={selectedAffiliates.length > 0 && selectedAffiliates.length < filteredAffiliates.length}
                    checked={selectedAffiliates.length === filteredAffiliates.length && filteredAffiliates.length > 0}
                    onChange={handleSelectAll}
                    style={{ color: '#1976d2' }}
                  />
                </TableCell>
                <TableCell style={{ color: '#1976d2', fontWeight: 'bold' }}>Name</TableCell>
                <TableCell style={{ color: '#1976d2', fontWeight: 'bold' }}>Email</TableCell>
                <TableCell style={{ color: '#1976d2', fontWeight: 'bold' }}>Referral Code</TableCell>
                <TableCell style={{ color: '#1976d2', fontWeight: 'bold' }}>Commission</TableCell>
                <TableCell style={{ color: '#1976d2', fontWeight: 'bold' }}>Earnings</TableCell>
                <TableCell style={{ color: '#1976d2', fontWeight: 'bold' }}>Status</TableCell>
                <TableCell style={{ color: '#1976d2', fontWeight: 'bold' }}>Created</TableCell>
                <TableCell style={{ color: '#1976d2', fontWeight: 'bold' }}>Conversions</TableCell>
                <TableCell style={{ color: '#1976d2', fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAffiliates.map((affiliate) => (
                <TableRow key={affiliate.id} hover style={{ backgroundColor: '#1a2332' }}>
                  <TableCell padding="checkbox" style={{ color: '#fff' }}>
                    <Checkbox
                      checked={selectedAffiliates.includes(affiliate.id)}
                      onChange={() => handleSelectAffiliate(affiliate.id)}
                      style={{ color: '#1976d2' }}
                    />
                  </TableCell>
                  <TableCell style={{ color: '#fff' }}>
                    <Box display="flex" alignItems="center">
                      <PersonIcon style={{ marginRight: 8, color: '#1976d2' }} />
                      <Typography variant="body2" style={{ color: '#fff' }}>{affiliate.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell style={{ color: '#fff' }}>
                    <Box display="flex" alignItems="center">
                      <EmailIcon style={{ marginRight: 8, color: '#1976d2' }} />
                      <Typography variant="body2" style={{ color: '#fff' }}>{affiliate.email}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell style={{ color: '#fff' }}>
                    <Box display="flex" alignItems="center">
                      <Typography variant="body2" style={{ fontFamily: 'monospace', color: '#fff' }}>
                        {affiliate.referralCode}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell style={{ color: '#fff' }}>
                    <Typography variant="body2" style={{ color: '#fff' }}>
                      ${affiliate.commissionAmount}
                    </Typography>
                  </TableCell>
                  <TableCell style={{ color: '#fff' }}>
                    <Typography variant="body2" style={{ fontFamily: 'monospace', color: '#fff' }}>
                      ${affiliate.totalEarnings.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell style={{ color: '#fff' }}>
                    <Chip
                      label={affiliate.status}
                      size="small"
                      style={{ 
                        backgroundColor: getStatusColor(affiliate.status),
                        color: getStatusTextColor(affiliate.status),
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                  <TableCell style={{ color: '#fff' }}>
                    <Typography variant="body2" style={{ color: '#fff' }}>
                      {format(new Date(affiliate.createdAt), 'MMM dd, yyyy')}
                    </Typography>
                  </TableCell>
                  <TableCell style={{ color: '#fff' }}>
                    <Box display="flex" alignItems="center">
                      <PerformanceIcon style={{ marginRight: 4, color: '#1976d2' }} />
                      <Typography variant="body2" style={{ color: '#fff' }}>
                        {affiliate.totalConversions} conv.
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell style={{ color: '#fff' }}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, affiliate)}
                      style={{ color: '#1976d2' }}
                    >
                      <MoreIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      <TablePagination
        component="div"
        rowsPerPageOptions={[5, 10, 25, 50]}
        count={filteredAffiliates.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        style={{ 
          backgroundColor: '#1a2332',
          color: '#fff',
          border: '1px solid #333',
          borderRadius: '8px',
          marginTop: '16px'
        }}
      />

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            backgroundColor: '#2d4a3e',
            color: '#fff',
            border: '1px solid #333'
          }
        }}
      >
        <MenuItem 
          onClick={() => {
            setShowDetailsDialog(true);
            handleMenuClose();
          }}
          style={{ color: '#fff', backgroundColor: '#2d4a3e' }}
        >
          <ViewIcon style={{ marginRight: 8, color: '#1976d2' }} />
          View Details
        </MenuItem>
        <MenuItem 
          onClick={() => {
            setShowConversionsDialog(true);
            handleMenuClose();
          }}
          style={{ color: '#fff', backgroundColor: '#2d4a3e' }}
        >
          <PerformanceIcon style={{ marginRight: 8, color: '#1976d2' }} />
          View Conversions
        </MenuItem>
        {selectedAffiliate?.status === 'PENDING' && (
          <MenuItem 
            onClick={() => handleApproveAffiliate(selectedAffiliate.id)}
            style={{ color: '#fff', backgroundColor: '#2d4a3e' }}
          >
            <ApproveIcon style={{ marginRight: 8, color: '#1976d2' }} />
            Approve
          </MenuItem>
        )}
        {selectedAffiliate?.status === 'APPROVED' && (
          <MenuItem 
            onClick={() => handleSuspendAffiliate(selectedAffiliate.id)}
            style={{ color: '#fff', backgroundColor: '#2d4a3e' }}
          >
            <SuspendIcon style={{ marginRight: 8, color: '#1976d2' }} />
            Suspend
          </MenuItem>
        )}
        <MenuItem 
          onClick={() => handleDeleteAffiliate(selectedAffiliate?.id || '')}
          style={{ color: '#fff', backgroundColor: '#2d4a3e' }}
        >
          <DeleteIcon style={{ marginRight: 8, color: '#f44336' }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Affiliate Details Dialog */}
      <Dialog
        open={showDetailsDialog}
        onClose={() => setShowDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Affiliate Details</DialogTitle>
        <DialogContent>
          {selectedAffiliate && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6">Basic Information</Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Name"
                      secondary={selectedAffiliate.name}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Email"
                      secondary={selectedAffiliate.email}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Phone"
                      secondary={selectedAffiliate.phoneNumber}
                    />
                  </ListItem>
                  {selectedAffiliate.company && (
                    <ListItem>
                      <ListItemText
                        primary="Company"
                        secondary={selectedAffiliate.company}
                      />
                    </ListItem>
                  )}
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6">Performance Metrics</Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Total Conversions"
                      secondary="0 (not available in this view)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Total Revenue"
                      secondary="$0.00 (not available in this view)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Total Earnings"
                      secondary="$0.00 (not available in this view)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Referral Code"
                      secondary={selectedAffiliate.referralCode}
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetailsDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Conversions Dialog */}
      <Dialog
        open={showConversionsDialog}
        onClose={() => setShowConversionsDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Conversions for {selectedAffiliate?.name} ({selectedAffiliate?.referralCode})
        </DialogTitle>
        <DialogContent>
          {conversionsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <Typography>Loading conversions...</Typography>
            </Box>
          ) : (conversionsData?.conversions && conversionsData.conversions.length > 0) ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Referral Code</TableCell>
                    <TableCell>Order Amount</TableCell>
                    <TableCell>Commission</TableCell>
                    <TableCell>Commission Rate</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created Date</TableCell>
                    <TableCell>Approved Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(conversionsData?.conversions && conversionsData.conversions.length > 0
                    ? conversionsData.conversions
                    : mockConversions).map((conversion: any) => (
                    <TableRow key={conversion.id}>
                      <TableCell>{conversion.referralCode}</TableCell>
                      <TableCell>${conversion.orderAmount.toFixed(2)}</TableCell>
                      <TableCell>${conversion.commissionAmount.toFixed(2)}</TableCell>
                      <TableCell>${conversion.commissionAmount}</TableCell>
                      <TableCell>
                        <Chip
                          label={conversion.status}
                          color={
                            conversion.status === 'approved' ? 'primary' :
                            conversion.status === 'paid' ? 'secondary' :
                            conversion.status === 'rejected' ? 'default' : 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{format(new Date(conversion.createdAt), 'MMM dd, yyyy')}</TableCell>
                      <TableCell>
                        {conversion.approvedAt ? format(new Date(conversion.approvedAt), 'MMM dd, yyyy') : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box display="flex" justifyContent="center" p={3}>
              <Typography color="textSecondary">
                No conversions found for this affiliate.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConversionsDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default observer(AffiliateManagement); 